// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • No invented numbers: a missing first-time fix rate showed "NaN%" and a NaN health bar;
//     missing values now show "—".
//   • Trend colours were wrong for backlog and MTTR (a rising backlog showed green). Each KPI now
//     knows whether up is good.
//   • The Sync Interval setting did nothing; it now changes the refresh interval, and auto-refresh
//     can be switched on from the settings.
//   • "Explain these KPIs" FlowSense AI explainer: rule findings from the KPIs and their change
//     since the last sync, plus a plain-language AI explanation.
// --- END UPDATE
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SimpleGrid,
  Box,
  Text,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Icon,
  useColorModeValue,
  Badge,
  Tooltip,
  Progress,
  HStack,
  VStack,
  Button,
  useToast,
  IconButton,
  Collapse,
  Code,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  FormControl,
  FormLabel,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  ButtonGroup,
} from '@chakra-ui/react';

// Services
import analyticsService from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE

import {
  MdTrendingUp,
  MdDoneAll,
  MdTimer,
  MdHistory,
  MdWarning,
  MdRefresh,
  MdSettings,
  MdBugReport,
  MdDownload,
  MdWifi,
  MdWifiOff,
} from 'react-icons/md';

// --- UPDATE
const numOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
// --- END UPDATE

/**
 * WorkOrderStatsWidget
 * Enhanced to receive WebSocket cues from the Parent Dashboard
 * with Advanced Debugging
 */
function WorkOrderStatsWidget({
  autoRefresh = false,
  refreshInterval = 60000,
  enableDebug = false,
  showTrends = true,
  isWsConnected = false, // <-- Passed from parent
  refreshTrigger = 0,    // <-- Passed from parent to trigger fetch
}) {
  // --- State ---
  const [stats, setStats] = useState(null);
  const [previousStats, setPreviousStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(refreshInterval / 1000);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebug, setShowDebug] = useState(enableDebug);
  
  const [performanceMetrics, setPerformanceMetrics] = useState({
    fetchTime: 0,
    lastUpdate: null,
    errorCount: 0,
    successCount: 0,
  });

  // 🚀 ADVANCED DEBUGGING: Render Tracking
  const renderCount = useRef(0);
  renderCount.current += 1;

  // --- UI State ---
  const [displayMode, setDisplayMode] = useState('cards');
  const [showAlerts, setShowAlerts] = useState(true);
  // --- UPDATE: the settings modal now really controls these
  const [interval, setIntervalMs] = useState(refreshInterval);
  const [autoOn, setAutoOn] = useState(autoRefresh);
  // --- END UPDATE

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const toast = useToast();

  // --- Refs for Concurrency Control ---
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  // Theme Constants
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const alertBg = useColorModeValue('orange.50', 'orange.900');
  const debugBg = useColorModeValue('gray.50', 'gray.700');

  // 🚀 ADVANCED DEBUGGING: Enhanced Debug Logger
  const addDebugLog = useCallback((message, type = 'info', details = null) => {
    if (!enableDebug && !showDebug) return;
    const timestamp = new Date().toISOString();
    
    // Safely parse details to prevent React state/render crashes with circular JSON
    let safeDetails = details;
    try {
      if (details instanceof Error) {
        safeDetails = { message: details.message, name: details.name, stack: details.stack };
      } else if (typeof details === 'object' && details !== null) {
        safeDetails = JSON.parse(JSON.stringify(details, (key, value) => 
          typeof value === 'function' ? '[Function]' : value
        ));
      }
    } catch(e) {
      safeDetails = "[Unserializable Object - See Browser Console]";
    }

    const log = { timestamp, type, message, details: safeDetails };
    // Increased buffer for deep trace analysis
    setDebugLogs(prev => [log, ...prev].slice(0, 100));
    
    if (type === 'error') {
      console.error(`[WOStatsWidget] ❌ ${message}`, details);
      console.trace(); // Forces a stack trace printout in the browser console
    } else if (type === 'warn') {
      console.warn(`[WOStatsWidget] ⚠️ ${message}`, details);
    } else {
      console.log(`[WOStatsWidget] ℹ️ ${message}`, details);
    }
  }, [enableDebug, showDebug]);

  // 🚀 ADVANCED DEBUGGING: Lifecycle Monitors
  useEffect(() => {
    if (renderCount.current % 5 === 0) {
       addDebugLog(`Component Rendered Cycle (Count: ${renderCount.current})`, 'info', { loading, refreshing });
    }
  });

  useEffect(() => {
    addDebugLog('Props/State Changed', 'info', {
      isWsConnected,
      refreshTrigger,
      hasInternalStats: !!stats,
      statsKeys: stats ? Object.keys(stats) : []
    });
  }, [isWsConnected, refreshTrigger, stats, addDebugLog]);

  // --- Core Data Fetcher ---
  const fetchStats = useCallback(async (isManual = false) => {
    if (fetchingRef.current) {
      addDebugLog('Fetch skipped: Request already in progress', 'warn');
      return;
    }

    const startTime = performance.now();

    try {
      fetchingRef.current = true;
      setRefreshing(true);
      setError('');

      addDebugLog(isManual ? 'Manual refresh triggered' : 'System refresh initiated', 'info');

      const response = await analyticsService.fetchWorkOrderStats();
      
      addDebugLog(`Raw payload received from API`, 'info', { 
        success: response?.success, 
        hasData: !!response?.data 
      });

      if (!mountedRef.current) return;

      if (response && response.success) {
        const fetchTime = performance.now() - startTime;

        setStats(prevStats => {
          if (prevStats) setPreviousStats(prevStats);
          return response.data;
        });
        
        setLastRefresh(new Date());
        setCountdown(interval / 1000); // Reset timer on success (--- UPDATE: chosen interval)

        setPerformanceMetrics(prev => ({
          ...prev,
          fetchTime: fetchTime.toFixed(2),
          lastUpdate: new Date(),
          successCount: prev.successCount + 1,
        }));

        addDebugLog('Stats synchronized', 'success', {
          latency: `${fetchTime.toFixed(2)}ms`,
          source: isManual ? 'manual' : 'auto',
          dataShape: response.data ? Object.keys(response.data) : null
        });

        if (isManual) {
          toast({ 
            title: 'Live Sync Complete', 
            status: 'success', 
            duration: 2000, 
            isClosable: true,
            position: 'bottom-right'
          });
        }
      } else {
        throw new Error(response?.message || 'Empty response from analytics engine');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMsg = err.message || 'Network error while fetching stats';
      setError(errorMsg);
      setPerformanceMetrics(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
      addDebugLog(`Fetch failed: ${errorMsg}`, 'error', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
        fetchingRef.current = false;
      }
    }
  }, [interval, addDebugLog, toast]);

  // --- Watch for Parent Event Triggers ---
  useEffect(() => {
    if (refreshTrigger > 0) {
      addDebugLog('Parent WebSocket event triggered sync', 'info', { triggerCount: refreshTrigger });
      fetchStats(false);
    }
  }, [refreshTrigger, fetchStats, addDebugLog]);

  // --- Trend Computation ---
  const trends = useMemo(() => {
    if (!stats || !previousStats || !showTrends) return null;
    
    // --- UPDATE: goodWhenUp — a rising backlog or MTTR is bad, a rising fix rate is good
    const calculateTrend = (current, previous, goodWhenUp = true) => {
      const c = numOrNull(current); const p = numOrNull(previous);
      if (c === null || p === null || p === 0) return null;
      const change = ((c - p) / p) * 100;
      if (change === 0) return null;
      return {
        direction: change > 0 ? 'increase' : 'decrease',
        percentage: Math.abs(change).toFixed(1),
        isPositive: goodWhenUp ? change > 0 : change < 0,
      };
    };

    return {
      openWOs: calculateTrend(stats.kpis?.total_open_wos, previousStats.kpis?.total_open_wos, false),
      completed: calculateTrend(stats.kpis?.completed_last_30_days, previousStats.kpis?.completed_last_30_days, true),
      avgResolution: calculateTrend(stats.kpis?.avg_resolution_time_hours, previousStats.kpis?.avg_resolution_time_hours, false),
      fixRate: calculateTrend(stats.kpis?.first_time_fix_rate, previousStats.kpis?.first_time_fix_rate, true),
    };
    // --- END UPDATE
  }, [stats, previousStats, showTrends]);

  // 🚀 ADVANCED DEBUGGING: Trace Trends Output
  useEffect(() => {
    addDebugLog('Trends Evaluated', 'success', { trends });
  }, [trends, addDebugLog]);

  // --- Lifecycle: Initial Load ---
  useEffect(() => {
    mountedRef.current = true;
    fetchStats();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchStats]); 

  // --- Lifecycle: Auto-Refresh Timer ---
  useEffect(() => {
    if (!autoOn || interval <= 0) return undefined; // --- UPDATE: settings-driven
    setCountdown(interval / 1000);

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchStats(); // Trigger fetch
          return interval / 1000; // Reset
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [autoOn, interval, fetchStats]);

  // --- Data Export ---
  const handleExport = useCallback(() => {
    if (!stats) return;
    try {
      const exportData = {
        meta: {
          timestamp: new Date().toISOString(),
          version: '2.1-ws',
          wsStatus: isWsConnected ? 'online' : 'offline'
        },
        stats: stats.kpis,
        trends,
        performance: performanceMetrics,
      };
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const link = document.createElement('a');
      link.href = dataUri;
      link.download = `flow-stats-${new Date().getTime()}.json`;
      link.click();
      
      addDebugLog('Data exported successfully', 'success');
      toast({ title: 'System snapshot exported', status: 'info', duration: 2000 });
    } catch (error) {
      addDebugLog(`Export failed: ${error.message}`, 'error', error);
      toast({ title: 'Export failed', description: error.message, status: 'error' });
    }
  }, [stats, trends, performanceMetrics, isWsConnected, addDebugLog, toast]);

  // --- Component Partials ---
  const renderStatCard = (config) => {
    const { icon, title, value, color, trend, helpText, alert } = config;

    return (
      <Stat
        bg={alert ? alertBg : cardBg}
        p={5}
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={alert ? 'orange.400' : borderColor}
        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
        _hover={{
          boxShadow: 'md',
          transform: 'translateY(-3px)',
          borderColor: alert ? 'orange.500' : 'blue.400',
        }}
      >
        <Flex justify="space-between" align="flex-start">
          <Box display="flex" alignItems="flex-start" flex="1">
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="14px"
              h="48px"
              w="48px"
              bg={color}
              color="white"
              me="16px"
              boxShadow="0px 4px 12px rgba(0, 0, 0, 0.1)"
            >
              {icon}
            </Flex>
            <Box flex="1">
              <HStack mb={1} justify="space-between">
                <StatLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" letterSpacing="wider">
                  {title}
                </StatLabel>
                {alert && (
                  <Badge colorScheme="orange" variant="filled" borderRadius="full" px={2}>
                    Critical
                  </Badge>
                )}
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="800">
                {value}
              </StatNumber>
              {helpText && (
                <StatHelpText fontSize="xs" color="gray.400" mt={1}>
                  {helpText}
                </StatHelpText>
              )}
              {trend && (
                <HStack mt={2} spacing={1}>
                  <StatArrow type={trend.direction === 'increase' ? 'increase' : 'decrease'} />
                  <Text fontSize="xs" fontWeight="bold" color={trend.isPositive ? "green.500" : "red.500"}>
                    {trend.percentage}%
                  </Text>
                  <Text fontSize="10px" color="gray.400">vs last sync</Text>
                </HStack>
              )}
            </Box>
          </Box>
        </Flex>

        {config.showHealth && (
          <Box mt={4}>
             <Flex justify="space-between" mb={1}>
               <Text fontSize="10px" fontWeight="bold">Efficiency Index</Text>
               <Text fontSize="10px" fontWeight="bold">{config.healthValue.toFixed(0)}%</Text>
             </Flex>
             <Progress
               value={config.healthValue}
               size="xs"
               colorScheme={config.healthValue > 75 ? 'green' : config.healthValue > 40 ? 'yellow' : 'red'}
               borderRadius="full"
               bg="gray.100"
             />
          </Box>
        )}
      </Stat>
    );
  };

  if (loading && !stats) {
    return (
      <Flex justify="center" align="center" h="300px" w="100%">
        <VStack spacing={4}>
          <Spinner thickness="4px" speed="0.8s" emptyColor="gray.200" color="blue.500" size="xl" />
          <Text fontWeight="medium" color="gray.500">Establishing Data Stream...</Text>
        </VStack>
      </Flex>
    );
  }

  const kpis = stats?.kpis || {}; // --- UPDATE: a response without kpis no longer crashes
  // --- UPDATE: real values or null
  const kOpen = numOrNull(kpis.total_open_wos);
  const kDone = numOrNull(kpis.completed_last_30_days);
  const kMttr = numOrNull(kpis.avg_resolution_time_hours);
  const kFtf = numOrNull(kpis.first_time_fix_rate);
  // --- END UPDATE

  const statConfigs = [
    {
      icon: <MdTrendingUp size={22} />,
      title: 'Active Backlog',
      value: kOpen ?? '—',
      color: 'blue.500',
      trend: trends?.openWOs,
      helpText: 'Open & In-Progress',
      alert: kOpen !== null && kOpen > 50,
    },
    {
      icon: <MdDoneAll size={22} />,
      title: 'Resolution Vol.',
      value: kDone ?? '—',
      color: 'green.500',
      trend: trends?.completed,
      helpText: 'Last 30 day window',
    },
    {
      icon: <MdTimer size={22} />,
      title: 'MTTR',
      value: kMttr === null ? '—' : `${kMttr.toFixed(1)}h`,
      color: kMttr !== null && kMttr > 48 ? 'orange.500' : 'teal.500',
      trend: trends?.avgResolution,
      alert: kMttr !== null && kMttr > 72,
      showHealth: kMttr !== null,
      healthValue: kMttr === null ? 0 : Math.max(0, 100 - (kMttr / 72) * 100),
    },
    {
      icon: <MdHistory size={22} />,
      title: 'FTF Rate',
      value: kFtf === null ? '—' : `${(kFtf * 100).toFixed(1)}%`,
      color: kFtf !== null && kFtf >= 0.8 ? 'purple.500' : 'yellow.500',
      trend: trends?.fixRate,
      alert: kFtf !== null && kFtf < 0.6,
      showHealth: kFtf !== null,
      healthValue: kFtf === null ? 0 : kFtf * 100,
    },
  ];

  // --- UPDATE: FlowSense rule findings (plain function — this runs after the early return above)
  const kpiFindings = [];
  if (kOpen !== null && kOpen > 50) kpiFindings.push({ severity: 'high', text: `Active backlog is ${kOpen} work orders (alert above 50).`, evidence: trends?.openWOs ? `${trends.openWOs.direction === 'increase' ? 'up' : 'down'} ${trends.openWOs.percentage}% since last sync` : undefined });
  if (kMttr !== null && kMttr > 72) kpiFindings.push({ severity: 'high', text: `Average resolution time is ${kMttr.toFixed(1)} h (alert above 72 h).` });
  else if (kMttr !== null && kMttr > 48) kpiFindings.push({ severity: 'medium', text: `Average resolution time is ${kMttr.toFixed(1)} h (watch above 48 h).` });
  if (kFtf !== null && kFtf < 0.6) kpiFindings.push({ severity: 'high', text: `First-time fix rate is ${(kFtf * 100).toFixed(1)}% (alert below 60%).` });
  else if (kFtf !== null && kFtf < 0.8) kpiFindings.push({ severity: 'medium', text: `First-time fix rate is ${(kFtf * 100).toFixed(1)}% (target 80%).` });
  if (trends?.completed && !trends.completed.isPositive) kpiFindings.push({ severity: 'info', text: `Completed work orders fell ${trends.completed.percentage}% since the last sync.` });
  [['total_open_wos', kOpen], ['completed_last_30_days', kDone], ['avg_resolution_time_hours', kMttr], ['first_time_fix_rate', kFtf]]
    .filter(([, v]) => v === null).forEach(([k]) => kpiFindings.push({ severity: 'info', text: `The server did not send ${k.replace(/_/g, ' ')}.` }));
  if (stats && !kpiFindings.some(x => ['high', 'medium'].includes(x.severity))) kpiFindings.push({ severity: 'ok', text: 'Backlog, resolution time and first-time fix are within their thresholds.' });
  const kpiFacts = { kpis, trends_since_last_sync: trends, websocket_connected: isWsConnected, last_sync: lastRefresh, thresholds: { backlog_alert: 50, mttr_watch_h: 48, mttr_alert_h: 72, ftf_target: 0.8, ftf_alert: 0.6 } };
  // --- END UPDATE

  return (
    <Box w="100%">
      {/* Widget Header */}
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <VStack align="start" spacing={0}>
          <HStack spacing={2}>
            <Heading size="md" letterSpacing="tight">Work Order Analytics</Heading>
            <Tooltip label={isWsConnected ? 'Connected to Live Stream' : 'WebSocket Offline'}>
               <Box>
                 <Icon 
                   as={isWsConnected ? MdWifi : MdWifiOff} 
                   color={isWsConnected ? "green.500" : "red.500"} 
                   boxSize={4}
                 />
               </Box>
            </Tooltip>
          </HStack>
          <Text fontSize="xs" color="gray.500">
            Real-time operational health monitor
          </Text>
        </VStack>

        <HStack spacing={2}>
          {autoOn && (
            <Badge colorScheme="blue" variant="outline" borderRadius="full" px={3} py={1}>
              Next Sync: {Math.floor(countdown)}s
            </Badge>
          )}
          <ButtonGroup size="sm" isAttached variant="outline">
            <Tooltip label="Force Refresh">
              <IconButton 
                icon={<MdRefresh />} 
                onClick={() => fetchStats(true)} 
                isLoading={refreshing}
              />
            </Tooltip>
            <Tooltip label="Download JSON">
              <IconButton icon={<MdDownload />} onClick={handleExport} />
            </Tooltip>
            <Tooltip label="Configure">
              <IconButton icon={<MdSettings />} onClick={onSettingsOpen} />
            </Tooltip>
          </ButtonGroup>
          {enableDebug && (
            <IconButton 
              size="sm"
              icon={<MdBugReport />} 
              onClick={() => setShowDebug(!showDebug)} 
              colorScheme={showDebug ? 'red' : 'gray'} 
              variant={showDebug ? 'solid' : 'outline'} 
            />
          )}
        </HStack>
      </Flex>

      {/* Stats Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        {statConfigs.map((config, idx) => (
          <React.Fragment key={idx}>{renderStatCard(config)}</React.Fragment>
        ))}
      </SimpleGrid>

      {/* --- UPDATE: FlowSense AI explainer */}
      {stats && (
        <Box mb={4}>
          <FlowSenseAIExplainer
            module="work_order_kpis"
            title="Explain these KPIs"
            subtitle="Backlog, throughput, MTTR and first-time fix"
            facts={kpiFacts}
            findings={kpiFindings}
            compact
            questions={['What is the biggest risk in these numbers?', 'Why might MTTR be rising?', 'What should the team focus on this week?']}
          />
        </Box>
      )}
      {/* --- END UPDATE */}

      {/* Critical Warnings */}
      {showAlerts && error && (
        <Alert status="error" variant="left-accent" mb={4} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold" fontSize="sm">Sync Interrupted</Text>
            <Text fontSize="xs">{error}</Text>
          </Box>
          <Button size="xs" colorScheme="red" variant="ghost" onClick={() => fetchStats(true)}>Reconnect</Button>
        </Alert>
      )}

      {/* Debug Console UI */}
      {enableDebug && (
        <Collapse in={showDebug} animateOpacity>
          <Box 
            p={4} 
            bg={debugBg} 
            borderRadius="xl" 
            borderWidth="1px" 
            borderColor={borderColor} 
            mt={4}
          >
            <Flex justify="space-between" align="center" mb={4}>
               <HStack>
                 <Badge colorScheme="purple">Debug Mode</Badge>
                 <Badge colorScheme="blue">Renders: {renderCount.current}</Badge>
                 <Text fontSize="xs" fontWeight="bold">Logs ({debugLogs.length})</Text>
               </HStack>
               <Button size="xs" variant="ghost" onClick={() => setDebugLogs([])}>Clear Buffer</Button>
            </Flex>
            <Accordion allowMultiple>
              <AccordionItem border="none">
                <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                  <Box flex="1" textAlign="left" fontSize="xs" fontWeight="bold">Performance Metrics</Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} px={0}>
                  <SimpleGrid columns={3} spacing={2}>
                    <Stat size="sm">
                      <StatLabel fontSize="10px">Latency</StatLabel>
                      <StatNumber fontSize="sm">{performanceMetrics.fetchTime}ms</StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel fontSize="10px">Success</StatLabel>
                      <StatNumber fontSize="sm">{performanceMetrics.successCount}</StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel fontSize="10px">Errors</StatLabel>
                      <StatNumber fontSize="sm" color="red.500">{performanceMetrics.errorCount}</StatNumber>
                    </Stat>
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Divider my={3} />
            <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto" className="custom-scrollbar">
              {debugLogs.map((log, i) => (
                <Box 
                   key={i} 
                   p={2} 
                   borderRadius="md" 
                   bg={
                     log.type === 'error' ? 'red.50' :
                     log.type === 'warn' ? 'yellow.50' :
                     log.type === 'success' ? 'green.50' : 'white'
                   }
                   borderWidth="1px"
                   borderColor={
                     log.type === 'error' ? 'red.200' :
                     log.type === 'warn' ? 'yellow.200' :
                     log.type === 'success' ? 'green.200' : 'gray.200'
                   }
                >
                  <Text fontWeight="bold" color={
                     log.type === 'error' ? 'red.700' :
                     log.type === 'warn' ? 'yellow.700' :
                     log.type === 'success' ? 'green.700' : 'gray.700'
                  }>
                    [{log.timestamp.split('T')[1].split('.')[0]}] {log.type.toUpperCase()}: {log.message}
                  </Text>
                  {log.details && (
                     <Code mt={1} p={1} w="100%" fontSize="10px" colorScheme="blackAlpha" whiteSpace="pre-wrap" display="block">
                        {JSON.stringify(log.details, null, 2)}
                     </Code>
                  )}
                </Box>
              ))}
              {debugLogs.length === 0 && <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>No activity logs recorded.</Text>}
            </VStack>
          </Box>
        </Collapse>
      )}

      {/* Settings Modal */}
      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader>Widget Configuration</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8}>
            <VStack spacing={6} align="stretch">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb="0" fontWeight="bold">Show Health Alerts</FormLabel>
                  <Text fontSize="xs" color="gray.500">Highlight KPIs exceeding thresholds</Text>
                </Box>
                <Switch isChecked={showAlerts} colorScheme="blue" onChange={(e) => setShowAlerts(e.target.checked)} />
              </FormControl>

              <Divider />

              <FormControl>
                <FormLabel fontWeight="bold">Sync Interval</FormLabel>
                {/* --- UPDATE: this select now changes the interval; auto-refresh switch added */}
                <Switch mb={2} isChecked={autoOn} onChange={(e) => setAutoOn(e.target.checked)}>Auto-refresh</Switch>
                <Select 
                  size="sm" 
                  borderRadius="md"
                  value={interval} 
                  onChange={(e) => {
                    setIntervalMs(Number(e.target.value));
                    toast({ title: "Sync interval updated", status: "info", duration: 1500 });
                  }}
                >
                  <option value={30000}>Aggressive (30s)</option>
                  <option value={60000}>Balanced (1m)</option>
                  <option value={300000}>Conservative (5m)</option>
                </Select>
              </FormControl>
              
              <Button colorScheme="blue" w="100%" onClick={onSettingsClose}>
                Save & Close
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default WorkOrderStatsWidget;